/**
 * Servicios base para operaciones CRUD en Firestore
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  DocumentData,
  Timestamp,
  serverTimestamp,
  writeBatch,
  onSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface BaseEntity {
  id: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
  createdBy?: string;
}

export interface PaginatedResult<T> {
  data: T[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * Convierte timestamps de Firestore a Date
 */
export function convertTimestamps<T extends DocumentData>(data: T): T {
  const result = { ...data } as Record<string, unknown>;
  Object.keys(result).forEach((key) => {
    if (result[key] instanceof Timestamp) {
      result[key] = (result[key] as Timestamp).toDate();
    } else if (
      result[key] &&
      typeof result[key] === "object" &&
      !Array.isArray(result[key])
    ) {
      result[key] = convertTimestamps(result[key] as DocumentData);
    }
  });
  return result as T;
}

/**
 * Obtiene un documento por ID
 */
export async function getById<T extends BaseEntity>(
  collectionName: string,
  id: string,
): Promise<T | null> {
  const docRef = doc(db, collectionName, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return convertTimestamps({
    id: docSnap.id,
    ...docSnap.data(),
  }) as T;
}

/**
 * Obtiene todos los documentos de una colección con filtros opcionales
 */
export async function getAll<T extends BaseEntity>(
  collectionName: string,
  constraints: QueryConstraint[] = [],
): Promise<T[]> {
  const q = query(collection(db, collectionName), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map(
    (doc) =>
      convertTimestamps({
        id: doc.id,
        ...doc.data(),
      }) as T,
  );
}

/**
 * Obtiene documentos paginados
 */
export async function getPaginated<T extends BaseEntity>(
  collectionName: string,
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot<DocumentData>,
  constraints: QueryConstraint[] = [],
): Promise<PaginatedResult<T>> {
  const baseConstraints = [...constraints, limit(pageSize + 1)];

  if (lastDocument) {
    baseConstraints.push(startAfter(lastDocument));
  }

  const q = query(collection(db, collectionName), ...baseConstraints);
  const snapshot = await getDocs(q);

  const hasMore = snapshot.docs.length > pageSize;
  const docs = hasMore ? snapshot.docs.slice(0, -1) : snapshot.docs;

  return {
    data: docs.map(
      (doc) =>
        convertTimestamps({
          id: doc.id,
          ...doc.data(),
        }) as T,
    ),
    lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
    hasMore,
  };
}

/**
 * Elimina recursivamente las claves cuyo valor es `undefined`.
 * Firestore rechaza `undefined`; preferimos omitir el campo o usar `null` explícito.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined)
      .map((item) => stripUndefined(item)) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    // No tocar instancias especiales (Timestamp, Date, FieldValue, etc.)
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      return value;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out as T;
  }
  return value;
}

/**
 * Crea un nuevo documento
 */
export async function create<T extends Omit<BaseEntity, "id">>(
  collectionName: string,
  data: Omit<T, "id" | "createdAt" | "updatedAt">,
  userId?: string,
): Promise<string> {
  const docData = stripUndefined({
    ...data,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId || null,
    updatedBy: userId || null,
  });

  const docRef = await addDoc(collection(db, collectionName), docData);
  return docRef.id;
}

/**
 * Crea un documento con ID específico
 */
export async function createWithId<T extends BaseEntity>(
  collectionName: string,
  id: string,
  data: Omit<T, "id" | "createdAt" | "updatedAt">,
  userId?: string,
): Promise<void> {
  const docRef = doc(db, collectionName, id);
  const docData = {
    ...data,
    id,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: userId || null,
  };

  await updateDoc(docRef, docData).catch(() => {
    // Si no existe, lo creamos
    return addDoc(collection(db, collectionName), { ...docData, id });
  });
}

/**
 * Actualiza un documento
 */
export async function update<T extends BaseEntity>(
  collectionName: string,
  id: string,
  data: Partial<Omit<T, "id" | "createdAt" | "updatedAt">>,
): Promise<void> {
  const docRef = doc(db, collectionName, id);
  await updateDoc(
    docRef,
    stripUndefined({
      ...data,
      updatedAt: serverTimestamp(),
    }),
  );
}

/**
 * Elimina un documento
 */
export async function remove(
  collectionName: string,
  id: string,
): Promise<void> {
  const docRef = doc(db, collectionName, id);
  await deleteDoc(docRef);
}

/**
 * Suscripción en tiempo real a un documento
 */
export function subscribeToDocument<T extends BaseEntity>(
  collectionName: string,
  id: string,
  callback: (data: T | null) => void,
): () => void {
  const docRef = doc(db, collectionName, id);

  return onSnapshot(docRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(null);
      return;
    }

    callback(
      convertTimestamps({
        id: snapshot.id,
        ...snapshot.data(),
      }) as T,
    );
  });
}

/**
 * Suscripción en tiempo real a una colección con filtros
 */
export function subscribeToCollection<T extends BaseEntity>(
  collectionName: string,
  callback: (data: T[]) => void,
  constraints: QueryConstraint[] = [],
  onError?: (error: Error) => void,
): () => void {
  const q = query(collection(db, collectionName), ...constraints);

  return onSnapshot(
    q,
    (snapshot) => {
      const data = snapshot.docs.map(
        (doc) =>
          convertTimestamps({
            id: doc.id,
            ...doc.data(),
          }) as T,
      );
      callback(data);
    },
    (error) => {
      console.error(
        `[Firestore] subscribeToCollection(${collectionName}) error:`,
        error,
      );
      if (onError) onError(error);
    },
  );
}

/**
 * Operaciones batch
 */
export function createBatch() {
  return writeBatch(db);
}

/**
 * Helper para construir queries
 */
export const queryHelpers = {
  where,
  orderBy,
  limit,
};
